import React, { useContext, useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import fromDtoToNcobEgiftSettings from 'dto/settings/fromDtoToNcobEgiftSettings.dto';
import fromDtoToNcobSettings from 'dto/settings/fromDtoToNcobSettings.dto';
import fromNcobEgiftSettingsToDto from 'dto/settings/fromNcobEgiftSettingsToDto.dto';
import fromNcobSettingsToDto from 'dto/settings/fromNcobSettingsToDto.dto';
import useAfn from 'hooks/useAfn.hook';
import useStateWhileMounted from 'hooks/useStateWhileMounted.hook';
import fromAnyToBool from 'utils/converters/fromAnyToBool.util';
import { fromAnyDateToTimestamp } from 'utils/converters/time/from.util';
import logger from 'utils/logger.util';
import { isTimeAfter } from 'utils/predicates/time/compare.util';

import beGetSettingsNcob from 'settings/actions/beGetSettingsNcob';
import bePutEgiftSettings from 'settings/actions/bePutEgiftSettings.action';
import bePutSettingsNcob from 'settings/actions/bePutSettingsNcob';

const L = logger('NcobSettingsProvider');

const Context = React.createContext();

// TODO: this is an indication that the "simple" scenario that it was first is now fit for a Redux store and reducer
const mergeValues = (options) => {
  const { egiftTime, settingsTime, valueEgift, valueSettings } = options ?? {};

  const timestampEgift = fromAnyDateToTimestamp(egiftTime);
  if (Number.isNaN(timestampEgift)) return valueSettings;

  const timestampSettings = fromAnyDateToTimestamp(settingsTime);
  if (Number.isNaN(timestampSettings)) return valueEgift;

  return isTimeAfter(timestampEgift, timestampSettings)
    ? { ...valueSettings, ...valueEgift }
    : { ...valueEgift, ...valueSettings };
};

const Provider = ({ beGetSettingsNcob, bePutSettingsNcob, bePutEgiftSettings, children, noAutoLoad }) => {
  const [settingsJson, setSettingsJson] = useStateWhileMounted();
  const [settingsTime, setSettingsTime] = useStateWhileMounted();
  const [egiftJson, setEgiftJson] = useStateWhileMounted();
  const [egiftTime, setEgiftTime] = useStateWhileMounted();

  const [{ error: errorRead, isLoading: isLoadingRead, isLoaded: isLoadedRead }, read] = useAfn(
    async (options) => {
      if (settingsJson && !options?.force) return { value: fromDtoToNcobSettings(settingsJson) };

      const r = await beGetSettingsNcob();
      const { ok } = r.is;

      if (!ok) {
        L.warn('read()', 'result:', r);
        return { ok, result: r, value: fromDtoToNcobSettings(settingsJson) };
      }

      setSettingsJson(r.json);
      setSettingsTime(r.receivedAt);

      return { ok, result: r, value: fromDtoToNcobSettings(r.json), json: r.json };
    },
    [beGetSettingsNcob, setSettingsJson, setSettingsTime, settingsJson],
  );

  const [{ error: errorUpdate, isLoading: isLoadingUpdate, isLoaded: isLoadedUpdate }, update] = useAfn(
    async (options) => {
      const r = await bePutSettingsNcob({ data: fromNcobSettingsToDto(options?.data) });
      const { ok } = r.is;

      if (!ok) {
        L.warn('update()', 'result:', r);
        return { ok, result: r, value: settingsJson, json: r.json };
      }

      setSettingsJson(r.json);
      setSettingsTime(r.receivedAt);

      return { ok, result: r, value: fromDtoToNcobSettings(r.json), json: r.json };
    },
    [bePutSettingsNcob, setSettingsJson, setSettingsTime, settingsJson],
  );

  const [{ error: errorEgift, isLoading: isLoadingEgift, isLoaded: isLoadedEgift }, updateEgift] = useAfn(
    async (options) => {
      const r = await bePutEgiftSettings({ data: fromNcobEgiftSettingsToDto(options?.data) });
      const { ok } = r.is;

      if (!ok) {
        L.warn('updateEgift()', 'result:', r);
        return { ok, result: r, value: egiftJson, json: r.json };
      }

      setEgiftJson(r.json);
      setEgiftTime(r.receivedAt);
      return { ok, result: r, value: fromDtoToNcobEgiftSettings(r.json), json: r.json };
    },
    [bePutEgiftSettings, egiftJson, setEgiftJson, setEgiftTime],
  );

  useEffect(() => void (!noAutoLoad && read()), [read, noAutoLoad]);

  const value = useMemo(() => {
    const isLoading = fromAnyToBool(isLoadingRead || isLoadingUpdate || isLoadingEgift);

    const valueSettings = fromDtoToNcobSettings(settingsJson);
    const valueEgift = fromDtoToNcobEgiftSettings(egiftJson);

    const value = mergeValues({ valueSettings, valueEgift, settingsTime, egiftTime });

    L.debug('useMemo()', value, '<-', valueEgift, '+', valueSettings, { settingsTime, egiftTime });

    return {
      value,
      valueSettings,
      valueEgift,
      error: errorRead || errorUpdate || errorEgift,
      isLoading,
      isLoaded: fromAnyToBool(!isLoading && (isLoadedRead || isLoadedUpdate || isLoadedEgift)),
      //
      read,
      readTime: settingsTime,
      errorRead,
      isLoadingRead,
      isLoadedRead,
      //
      update,
      updateTime: settingsTime,
      errorUpdate,
      isLoadingUpdate,
      isLoadedUpdate,
      //
      updateEgift,
      updateEgiftTime: egiftTime,
      errorEgift,
      isLoadingEgift,
      isLoadedEgift,
    };
  }, [
    isLoadingRead,
    isLoadingUpdate,
    isLoadingEgift,
    settingsJson,
    egiftJson,
    settingsTime,
    egiftTime,
    errorRead,
    errorUpdate,
    errorEgift,
    isLoadedRead,
    isLoadedUpdate,
    isLoadedEgift,
    read,
    update,
    updateEgift,
  ]);

  if (value.error) L.warn('()', value);

  return <Context.Provider value={value}>{children}</Context.Provider>;
};

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      beGetSettingsNcob,
      bePutSettingsNcob,
      bePutEgiftSettings,
    },
    dispatch,
  );

export const NcobSettingsProvider = connect(null, mapDispatchToProps)(Provider);

export const useNcobSettings = () => useContext(Context);
